import { Icon, IconSVG, SVGColorKey } from "@studio3t/generic-components";
import { type ReactNode, useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { AccessDeniedPage } from "@/components/AccessDeniedPage";
import { getProviders, isAuthConfigured, redirectToLogin } from "./oidc";
import type { AuthStatus } from "./types";
import { useAuth } from "./useAuth";
import { useUserAccessCheck } from "./useUserAccessCheck";

function getReturnUrl(): string {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (!isAuthConfigured()) return <>{children}</>;

  return <AuthGuardInner status={status}>{children}</AuthGuardInner>;
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.08 24.08 0 0 0 0 21.56l7.98-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function GitHubLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z"
      />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function KeyLogo() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}

const KNOWN_ICONS: Record<string, () => JSX.Element> = {
  google: GoogleLogo,
  github: GitHubLogo,
  microsoft: MicrosoftLogo,
};

function ProviderIcon({ providerId }: { providerId: string }) {
  const key = providerId.toLowerCase();
  const LogoComponent = KNOWN_ICONS[key];
  return LogoComponent ? <LogoComponent /> : <KeyLogo />;
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: ${(p) => p.theme.semanticAppBackground};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 48px 40px;

  background: ${(p) => p.theme.semanticAppBackground};
  border: none;
  border-radius: 12px;
  min-width: 320px;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  > div:first-child {
    display: flex;
    justify-content: center;
  }
`;

const AppName = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 24px;
  height: 44px;
  min-width: 240px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease;

  svg {
    flex-shrink: 0;
    display: block;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const GoogleButton = styled(SignInButton)`
  border: 1px solid #dadce0;
  background: #fff;
  color: #3c4043;
  font-family: "Google Sans", Roboto, Arial, sans-serif;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3);
  }

  &:active:not(:disabled) {
    background: #f1f3f4;
  }
`;

const ProviderButton = styled(SignInButton)`
  border: 1px solid ${(p) => p.theme.sidebarBorder};
  background: transparent;
  color: ${(p) => p.theme.semanticTextBody};
  font-family: inherit;

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: ${(p) => p.theme.badgeErrorSubtleColor};
  margin: 0;
`;

const RetryButton = styled.button`
  padding: 6px 16px;
  border: 1px solid ${(p) => p.theme.sidebarBorder};
  border-radius: 6px;
  background: transparent;
  color: ${(p) => p.theme.semanticTextBody};
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: ${(p) => p.theme.semanticTextLight};
  margin: 0;
`;

function LoginPage() {
  const redirectingRef = useRef(false);
  const [redirectFailed, setRedirectFailed] = useState(false);
  const providers = getProviders();

  const handleLogin = useCallback((provider: OidcProvider) => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    setRedirectFailed(false);
    redirectToLogin(provider, getReturnUrl()).catch((err) => {
      console.error("Failed to redirect to login:", err);
      redirectingRef.current = false;
      setRedirectFailed(true);
    });
  }, []);

  // No auto-redirect, even with a single provider: always render the Sign-in
  // button so the user explicitly initiates login.
  return (
    <Page>
      <Card>
        <Brand>
          <IconSVG
            icon={Icon.Manatee}
            size={36}
            svgColorKey={SVGColorKey.buttonPrimaryIcon}
          />
          <AppName>3TL Bridge</AppName>
        </Brand>

        <ProviderList>
          {providers.map((provider) =>
            provider.id.toLowerCase() === "google" ? (
              <GoogleButton
                key={provider.id}
                type="button"
                onClick={() => handleLogin(provider)}
              >
                <GoogleLogo />
                Sign in with Google
              </GoogleButton>
            ) : (
              <ProviderButton
                key={provider.id}
                type="button"
                onClick={() => handleLogin(provider)}
              >
                <ProviderIcon providerId={provider.id} />
                Sign in with {provider.name}
              </ProviderButton>
            ),
          )}
        </ProviderList>

        {redirectFailed && (
          <>
            <ErrorText>Unable to redirect to login</ErrorText>
            <RetryButton
              type="button"
              onClick={() => {
                const first = providers[0];
                if (first) handleLogin(first);
              }}
            >
              Try again
            </RetryButton>
          </>
        )}
      </Card>
    </Page>
  );
}

function AuthGuardInner({
  children,
  status,
}: {
  children: ReactNode;
  status: AuthStatus;
}) {
  if (status === "authenticated")
    return <UserAccessGate>{children}</UserAccessGate>;

  if (status === "unauthenticated") return <LoginPage />;

  return (
    <Page>
      <LoadingText>Loading…</LoadingText>
    </Page>
  );
}

/**
 * Post-login gate: verifies the signed-in user's per-user entitlement before
 * rendering the app. Shows a loading state while checking, and AccessDeniedPage
 * on denial. When per-user enforcement is off, the backend returns
 * USER_IS_ENTITLED and this passes through transparently.
 */
function UserAccessGate({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const { state, retry } = useUserAccessCheck();

  if (state.status === "granted") return <>{children}</>;

  if (state.status === "denied") {
    return (
      <AccessDeniedPage
        kind={state.kind}
        onRetry={state.kind === "retryable" ? retry : undefined}
        onSignOut={logout}
      />
    );
  }

  return (
    <Page>
      <LoadingText>Verifying access…</LoadingText>
    </Page>
  );
}
